/**
 * MiniCard - Miniature card rendering for replay timeline thumbnails
 * 
 * For trumps and face cards (J, C, Q, K): shows a zoomed top-left portion of the card image
 * For pip cards (1-10 of hearts/spades/diamonds/clubs): shows the number + suit symbol
 * For the Excuse: shows zoomed top-left of the card image
 */

import { memo } from 'react';
import type { CardNumber } from '../types/game';
import { Suit } from '../types/game';
import { getCardInfo, getCardImagePath, getCardBackImagePath } from '../utils/cards';

interface MiniCardProps {
  card: CardNumber;
  className?: string;
  style?: React.CSSProperties;
}

const SUIT_SYMBOLS: Record<number, string> = {
  [Suit.Hearts]: '♥',
  [Suit.Spades]: '♠',
  [Suit.Diamonds]: '♦',
  [Suit.Clubs]: '♣',
};

const SUIT_CSS: Record<number, string> = {
  [Suit.Hearts]: 'suit-hearts',
  [Suit.Spades]: 'suit-spades',
  [Suit.Diamonds]: 'suit-diamonds',
  [Suit.Clubs]: 'suit-clubs',
  [Suit.Trumps]: 'suit-trumps',
};

const MiniCard = memo(function MiniCard({ card, className = '', style }: MiniCardProps) {
  const info = getCardInfo(card);
  const suitClass = SUIT_CSS[info.suit] || '';
  
  // Use zoomed image for: trumps (including Excuse), face cards (J=11, C=12, Q=13, K=14)
  const usesImage = info.isTrump || info.isExcuse || info.value >= 11;
  
  if (usesImage) {
    return (
      <div className={`mini-card ${suitClass} ${className}`} style={style}>
        <img
          src={getCardImagePath(card)}
          alt=""
          className="mini-card-image"
          draggable={false}
        />
      </div>
    );
  }
  
  // Pip cards: show number + suit symbol
  const suitSymbol = SUIT_SYMBOLS[info.suit] || '';
  const valueStr = info.value === 1 ? 'A' : String(info.value);
  
  return (
    <div className={`mini-card ${suitClass} ${className}`} style={style}>
      <div className="mini-card-pip">
        <span className="mini-card-pip-value">{valueStr}</span>
        <span className="mini-card-pip-suit">{suitSymbol}</span>
      </div>
    </div>
  );
});

export default MiniCard;

/** Back-of-card mini thumbnail (for distribution step) */
export const MiniCardBack = memo(function MiniCardBack() {
  return (
    <div className="mini-card mini-card-back">
      <img src={getCardBackImagePath()} alt="" draggable={false} />
    </div>
  );
});
